<template>
  <div class="container mx-auto px-8">
    <h1 class="page-header">Auto-retweeter</h1>

    <div class="card tweet flex flex-row p-3 shadow-lg">
      <base-alert
        type="warning"
        radius="rounded-lg"
        class="my-4"
        v-show="userProfile.customerStatus === 'none'"
        data-cy="auto-retweeter-subscription-alert"
      >
        <p>You are currently on the {{ userPlan }} plan.</p>
        <p>
          The auto-retweeter won't work unless you
          <router-link data-cy="auto-retweeter-upgrade-link" to="/billing"
            >grab a {{ getPlanLabel('standard') }} Aerielab subscription</router-link
          >.
        </p>
      </base-alert>

      <div>
        <p>
          Use this tool to automatically retweet another Twitter account (e.g. company account) when
          it tweets through Aerielab.
        </p>
      </div>

      <div class="container-fluid mt-6 space-y-6">
        <div data-cy="retweeting" v-if="usernames.length > 0">
          You are currently retweeting
          <span v-for="(username, index) in usernames" :key="`username-${index}`">
            @{{ username }}
            <template v-if="index !== usernames.length - 1">
              <template v-if="index === usernames.length - 2"> and </template>
              <template v-else>, </template>
            </template> </span
          >.<br /><br />
          Every tweet they post
          <i>through Aerielab</i> will be retweeted by your account @{{ userProfile.username }}.
        </div>

        <div data-cy="is-updating" v-else-if="isUpdating">
          Updating data...
        </div>

        <div data-cy="not-retweeting" v-else>
          You are currently not retweeting any account. Add one below.
        </div>

        <div class="flex items-center">
          <div class="w-3/4">
            <base-input
              data-cy="retweeter-input"
              placeholder="Twitter handle"
              v-model.trim="username"
              @keyup.enter="
                !isUpdating && canAddAccountsToRetweet ? addUserToRetweet() : null
              "
            ></base-input>
          </div>

          <div class="flex w-1/4 gap-x-1">
            <base-button
              data-cy="add-user-button"
              @click="addUserToRetweet"
              :disabled="isUpdating || !canAddAccountsToRetweet"
              class="btn text primary ml-auto lg:mt-0"
            >
              <span>Add user</span>
            </base-button>
            <base-button
              data-cy="save-to-retweet"
              @click="saveUserToRetweet"
              :loading="isUpdating"
              :disabled="isUpdating || !canSaveAccountsToRetweet"
              class="btn text primary ml-auto lg:mt-0"
            >
              <span>Save</span>
            </base-button>
          </div>
        </div>

        <div v-if="selectedUsersToShow.length > 0" class="flex flex-wrap gap-2">
          <div
            data-cy="selected-user"
            v-for="(user, i) in selectedUsersToShow"
            :key="i"
            class="relative"
          >
            <tooltip :content="`Stop retweeting ${user.username}`">
              <button
                data-cy="stop-retweeting"
                class="absolute right-0 top-0 z-10"
                @click="removeUser(user)"
              >
                <inline-svg src="/img/icons/close.svg" class="mr-1 mt-1 h-3 w-3" />
              </button>
            </tooltip>
            <base-button @click="removeUser(user)" class="btn text primary w-full sm:w-auto">
              <span>@{{ user.username }}</span>
            </base-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import controller from '@/controller';
  import dao from '@/dao';
  import SwalModalMixin from '../Mixins/SwalModalMixin';
  import { AxiosError } from 'axios';
  import CustomerStatusMixin from '../Mixins/CustomerStatusMixin.vue';

  const USER_ID_WITH_SPECIAL_GRANT = 't1Bo1NKK8XYlS9emkPsXfV3ATUk2';

  export default {
    name: 'auto-retweeter',
    mixins: [SwalModalMixin, CustomerStatusMixin],
    computed: {
      ...mapGetters({ currentUser: 'getCurrentUser', userProfile: 'getUserProfile' }),
      selectedUsersToShow() {
        return this.selectedUsers.filter((user) => user.action !== 'remove');
      },
      limitPerPlan() {
        if (this.userProfile.uid === USER_ID_WITH_SPECIAL_GRANT) {
          return 10;
        } else {
          return 3;
        }
      },
      canSaveAccountsToRetweet() {
        if (this.selectedUsers.length !== this.usernames.length) {
          return true;
        }
        return !this.selectedUsers.every((u) => {
          if (u.action && u.action === 'remove') {
            return false;
          }
          return Boolean(this.accountsToRetweet[u.username]);
        });
      },
      usernames() {
        return Object.values(this.accountsToRetweet).map((u) => u.username);
      },
      canAddAccountsToRetweet() {
        return Boolean(this.username) && this.selectedUsers.length < this.limitPerPlan;
      },
    },
    data() {
      return {
        selectedUsers: [],
        username: '',
        accountsToRetweet: {},
        isUpdating: false,
      };
    },
    methods: {
      removeUser: function (user) {
        const userExistsInDB = Boolean(this.accountsToRetweet[user.username]);
        if (userExistsInDB) {
          this.selectedUsers = this.selectedUsers.map((u) => {
            if (u.username === user.username) {
              return { ...u, action: 'remove' };
            }
            return u;
          });
        } else {
          this.selectedUsers = this.selectedUsers.filter((u) => u.username !== user.username);
        }
      },
      updateAccountsToRetweet: async function (idsToRemove) {
        const updatedAccountsToBeRetweeted = this.userProfile.accountsToBeRetweeted.filter(
          (id) => !idsToRemove.includes(id)
        );
        await dao.userProfile.updateAccountsToRetweet(
          this.userProfile.uid,
          updatedAccountsToBeRetweeted
        );
        this.$eventStore.removeAnAccountToRetweet();
      },
      saveUserToRetweet: async function () {
        this.isUpdating = true;
        try {
          const [usersToBeRemoved, usersToAdd] = this.selectedUsers.reduce(
            (acc, user) => {
              if (user.action === 'remove') {
                acc[0].push(this.accountsToRetweet[user.username].id);
              } else if (Boolean(this.accountsToRetweet[user.username]) === false) {
                acc[1].push(user.username);
              }
              return acc;
            },
            [[], []]
          );
          if (usersToBeRemoved.length > 0) {
            await this.updateAccountsToRetweet(usersToBeRemoved);
          }
          if (usersToAdd.length > 0) {
            const { userIds } = await controller.selectUsersToRetweet(
              this.currentUser,
              this.userProfile,
              usersToAdd
            );
            this.selectedUsers = this.selectedUsers.map((user, i) => {
              return {
                ...user,
                id: userIds[i],
              };
            });
          }
          const message = `Auto retweeter settings saved.`;
          this.$eventStore.addAnAccountToRetweet();
          this.$notify({ type: 'success', message });
        } catch (error) {
          let message = 'An error occurred while saving Auto retweeter settings.';
          if (error instanceof AxiosError && error.response.status === 400) {
            let userNotFound = '';
            error.response.data.usernames.forEach((username) => {
              userNotFound += `<li>${username}</li>`;
            });
            message = `The following users were not found: <ul>${userNotFound}</ul>`;
          }
          this.swalModal({
            title: 'Error',
            html: message,
            type: 'warning',
          });
        } finally {
          this.isUpdating = false;
          this.username = '';
          this.selectedUsers = this.selectedUsers.filter((u) => u.action !== 'remove');
          this.accountsToRetweet = {};
          this.selectedUsers.forEach((user) => {
            this.accountsToRetweet[user.username] = user;
          });
        }
      },
      addUserToRetweet() {
        this.selectedUsers.push({
          username: this.username,
          action: 'add',
        });
        this.username = '';
      },
    },
    async created() {
      const nonExistentAccounts = [];
      const handleError = (error, twitterId) => {
        if ([403, 404].includes(error.response.status)) {
          nonExistentAccounts.push(twitterId);
        }
      };

      if (this.userProfile.accountsToBeRetweeted.length > 0) {
        const promises = this.userProfile.accountsToBeRetweeted.map((twitterId) => {
          return controller
            .getTwitterUserInfo(this.currentUser, this.userProfile, twitterId)
            .catch((error) => {
              handleError(error, twitterId);
            });
        });

        this.isUpdating = true;

        const usersInfo = (await Promise.all(promises)).filter((result) => Boolean(result));
        this.accountsToRetweet = usersInfo.reduce((acc, userInfo) => {
          acc = {
            ...acc,
            [userInfo.username]: {
              id: userInfo.id,
              username: userInfo.username,
            },
          };
          return acc;
        }, {});
        this.selectedUsers = [...Object.values(this.accountsToRetweet)];

        this.isUpdating = false;

        if (nonExistentAccounts.length) {
          return this.swalModal({
            title: 'Error',
            html: 'Some accounts that you saved before do not exist anymore or are suspended by Twitter.',
          }).then((result) => {
            const updatedAccountsToBeRetweeted = this.userProfile.accountsToBeRetweeted.filter(
              (id) => !nonExistentAccounts.includes(id)
            );
            return dao.userProfile.updateAccountsToRetweet(
              this.userProfile.uid,
              updatedAccountsToBeRetweeted
            );
          });
        }
      }
    },
    mounted() {
      this.$eventStore.visitToolboxAutoRetweeterPage();
    },
  };
</script>
